import React, { useState, useEffect } from "react";
import {
  Star,
  MapPin,
  Users,
  Calendar,
  TrendingUp,
  Shield,
  Clock,
  ArrowRight,
  Phone,
  Menu,
  X,
  Camera,
  Music,
  Utensils,
  Home,
  Award,
  PlayCircle,
  ChevronRight,
  Eye,
  Heart,
  Download,
  Smartphone,
  IndianRupee,
  BarChart3,
  Target,
  MessageCircle,
  CheckCircle2,
  Calendar1Icon,
  StarIcon,
  UsersIcon,
  TrendingUpIcon,
  Package2,
  Building2,
  UtensilsCrossed,
  Palette,
  Gem,
  Check,
  Bot,
} from "lucide-react";
import Header from "../components/Layout/Header";
import SiteFooter from "../components/Layout/SiteFooter";
import Reveal from "../components/UI/Reveal";
import { useNavigate } from "react-router-dom";
import useSEO from "../hooks/useSEO";

const CARD = "bg-white rounded-2xl border border-gray-200/70 shadow-[0_1px_3px_rgba(16,24,40,0.04)]";
const BTN_PRIMARY =
  "group inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 font-['Manrope'] font-semibold text-white shadow-lg shadow-purple-600/20 transition-all duration-300 hover:shadow-xl hover:shadow-purple-600/30";

// Section heading: small tag, big title, supporting line
const SectionHead = ({ eyebrow, title, text, center = false }) => (
  <Reveal className={`max-w-2xl mb-12 sm:mb-14 ${center ? "mx-auto text-center" : ""}`}>
    <span className="inline-flex items-center gap-2 rounded-full border border-purple-200 bg-purple-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-gray-800 mb-5">
      <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-purple-600 to-pink-600" />
      {eyebrow}
    </span>
    <h2 className="font-['Manrope'] text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 tracking-tight leading-[1.1]">
      {title}
    </h2>
    {text && <p className="mt-4 text-base sm:text-lg text-gray-600 leading-relaxed">{text}</p>}
  </Reveal>
);

const MandapAIVendor = () => {
  useSEO({
    description:
      "MandapAI connects you with verified wedding & event vendors — venues, caterers, photographers and decorators across 500+ Indian cities. Vendors get AI tools to grow bookings up to 300%.",
    path: "/",
  });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [currentStat, setCurrentStat] = useState(0);
  const [aiTab, setAiTab] = useState("vendor")
  const [activeVendor, setActiveVendor] = useState(0);
  const [vendorPaused, setVendorPaused] = useState(false);
  const navigate = useNavigate();

  // Solid accent palette for the vendor showcase (index-aligned to vendorTypes)
  const vendorAccents = [
    { text: "text-purple-600", soft: "bg-purple-50", line: "bg-purple-500", border: "border-purple-200", ring: "ring-purple-100" },
    { text: "text-purple-600", soft: "bg-purple-50", line: "bg-purple-500", border: "border-purple-200", ring: "ring-purple-100" },
    { text: "text-emerald-600", soft: "bg-emerald-50", line: "bg-emerald-500", border: "border-emerald-200", ring: "ring-emerald-100" },
    { text: "text-orange-600", soft: "bg-orange-50", line: "bg-orange-500", border: "border-orange-200", ring: "ring-orange-100" },
  ];
  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Auto-rotate stats
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStat((prev) => (prev + 1) % stats.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Auto-rotate vendor showcase (pauses while the user hovers it)
  useEffect(() => {
    if (vendorPaused) return;
    const interval = setInterval(() => {
      setActiveVendor((prev) => (prev + 1) % vendorTypes.length);
    }, 3500);
    return () => clearInterval(interval);
  }, [vendorPaused]);

  const features = [
    // {
    //   icon: <TrendingUp className="w-6 h-6 sm:w-8 sm:h-8" />,
    //   title: "Revenue Optimization",
    //   description:
    //     "AI-powered pricing suggestions and demand forecasting to maximize your earnings",
    //   highlight: "+300% Revenue Boost",
    //   color: "from-purple-400 to-pink-500",
    // },
    // {
    //   icon: <UsersIcon className="w-6 h-6 sm:w-8 sm:h-8" />,
    //   title: "Customer Acquisition",
    //   description:
    //     "Access to 100,000+ active customers and automated marketing campaigns",
    //   highlight: "50K+ Monthly Leads",
    //   color: "from-purple-400 to-pink-500",
    // },
    {
      icon: <Calendar className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "Smart Management",
      description:
        "Automated scheduling, inventory management, and real-time availability updates",
      highlight: "Zero Double Bookings",
      color: "from-purple-400 to-pink-500",
    },
    // {
    //   icon: <Shield className="w-6 h-6 sm:w-8 sm:h-8" />,
    //   title: "Secure Transactions",
    //   description:
    //     "Bank-grade security with instant payouts and fraud protection",
    //   highlight: "100% Payment Security",
    //   color: "from-purple-400 to-pink-500",
    // },
    {
      icon: <BarChart3 className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "Analytics Dashboard",
      description:
        "Real-time insights, performance metrics, and business intelligence tools",
      highlight: "Advanced Analytics",
      color: "from-purple-400 to-pink-500",
    },
    {
      icon: <CheckCircle2 className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "Instant Notifications",
      description:
        "Real-time booking alerts, customer messages",
      highlight: "Lightning Fast Updates",
      color: "from-purple-400 to-pink-500",
    },
  ];

  const vendorTypes = [
    {
      title: "Venue Owners",
      icon: <Home className="w-8 h-8 sm:w-12 sm:h-12" />,
      items: [
        "Convention Halls",
        "Banquet Halls",
        "Lawns & Gardens",
        "Hotel Venues",
        "Farmhouses",
        "Beach Resorts",
      ],
      earning: "₹50,000 - ₹2,00,000",
      color: "from-purple-500 via-pink-500 to-pink-500",
      growth: "+250%",
    },
    {
      title: "Catering Services",
      icon: <Utensils className="w-8 h-8 sm:w-12 sm:h-12" />,
      items: [
        "Wedding Catering",
        "Corporate Events",
        "Veg/Non-Veg Packages",
        "Custom Menus",
        "Live Counters",
        "Dessert Bars",
      ],
      earning: "₹30,000 - ₹1,50,000",
      color: "from-purple-500 via-pink-500 to-pink-500",
      growth: "+320%",
    },
    {
      title: "Photography Services",
      icon: <Camera className="w-8 h-8 sm:w-12 sm:h-12" />,
      items: [
        "Wedding Photography",
        "Event Coverage",
        "Drone Services",
        "Live Streaming",
        "Photo Booths",
        "Cinematic Videos",
      ],
      earning: "₹25,000 - ₹1,00,000",
      color: "from-purple-500 via-pink-500 to-pink-500",
      growth: "+280%",
    },
    {
      title: "Decoration Services",
      icon: <Music className="w-8 h-8 sm:w-12 sm:h-12" />,
      items: [
        "Decoration",
        "Music & DJ",
        "Lighting",
        "Entertainment",
        "Security",
        "Transportation",
      ],
      earning: "₹15,000 - ₹75,000",
      color: "from-purple-500 via-pink-500 to-pink-500",
      growth: "+200%",
    },
  ];

  const stats = [
    {
      number: "25,000+",
      label: "Active Vendors",
      icon: <UsersIcon className="w-5 h-5 sm:w-6 sm:h-6" />,
    },
    {
      number: "1,50,000+",
      label: "Events Completed",
      icon: <Calendar className="w-5 h-5 sm:w-6 sm:h-6" />,
    },
    {
      number: "₹1,200Cr+",
      label: "Revenue Generated",
      icon: <IndianRupee className="w-5 h-5 sm:w-6 sm:h-6" />,
    },
    {
      number: "4.9",
      label: "Average Rating",
      icon: <Star className="w-5 h-5 sm:w-6 sm:h-6" />,
    },
    {
      number: "500+",
      label: "Cities Covered",
      icon: <MapPin className="w-5 h-5 sm:w-6 sm:h-6" />,
    },
    {
      number: "24/7",
      label: "Customer Support",
      icon: <Clock className="w-5 h-5 sm:w-6 sm:h-6" />,
    },
  ];

  const testimonials = [
    {
      name: "Rajesh Kumar",
      business: "Royal Banquet Hall, Mumbai",
      image: "RK",
      rating: 5,
      text: "MandapAI transformed my business completely! From 2-3 bookings per month to 15-20 bookings. The platform's reach is incredible and the support team is always there to help.",
      revenue: "₹8L/month",
      growth: "+400%",
    },
    {
      name: "Priya Sharma",
      business: "Delicious Catering Services, Delhi",
      image: "PS",
      rating: 5,
      text: "The automated booking system and customer management tools have saved me hours of work every day. Plus, the commission structure is very fair and transparent.",
      revenue: "₹5L/month",
      growth: "+350%",
    },
    {
      name: "Amit Patel",
      business: "Capture Moments Photography, Bangalore",
      image: "AP",
      rating: 5,
      text: "I've been able to showcase my portfolio to thousands of potential customers. The quality of leads is excellent and the payment system is super reliable.",
      revenue: "₹3L/month",
      growth: "+280%",
    },
    {
      name: "Neha Gupta",
      business: "Dream Decorators, Pune",
      image: "NG",
      rating: 5,
      text: "The analytics dashboard helps me understand market trends and optimize my pricing. I've increased my revenue by 300% in just 6 months!",
      revenue: "₹4L/month",
      growth: "+300%",
    },
  ];

  const howItWorks = [
    {
      step: "01",
      title: "Quick Registration",
      description:
        "Sign up in under 5 minutes with our streamlined onboarding process. Upload your documents and business details.",
      icon: <Download className="w-6 h-6 sm:w-8 sm:h-8" />,
      time: "5 minutes",
    },
    {
      step: "02",
      title: "Create Stunning Listings",
      description:
        "Use our professional listing builder with photo editing tools, package customization, and pricing optimization.",
      icon: <Star className="w-6 h-6 sm:w-8 sm:h-8" />,
      time: "30 minutes",
    },
    {
      step: "03",
      title: "Get Verified",
      description:
        "Our quality assurance team reviews your profile within 24 hours to ensure the highest standards.",
      icon: <Award className="w-6 h-6 sm:w-8 sm:h-8" />,
      time: "24 hours",
    },
    {
      step: "04",
      title: "Start Earning",
      description:
        "Go live and start receiving bookings immediately. Track earnings, manage customers, and grow your business.",
      icon: <TrendingUp className="w-6 h-6 sm:w-8 sm:h-8" />,
      time: "Instant",
    },
  ];

  const benefits = [
    {
      title: "Zero Marketing Costs",
      description: "We handle all marketing and customer acquisition for you",
      icon: <Target className="w-5 h-5 sm:w-6 sm:h-6" />,
    },
    {
      title: "Instant Credibility",
      description: "Verified badge and customer reviews build trust instantly",
      icon: <Shield className="w-5 h-5 sm:w-6 sm:h-6" />,
    },
    {
      title: "Mobile-First Design",
      description: "Manage your business on-the-go with our mobile app",
      icon: <Smartphone className="w-5 h-5 sm:w-6 sm:h-6" />,
    },
    {
      title: "Analytics Dashboard",
      description: "Unlock growth with powerful insights and customizable performance dashboards",
      icon: <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6" />,
    },
  ];

  const pricingPlans = [
    {
      name: "Starter",
      commission: "12%",
      features: [
        "Basic listing",
        "Standard support",
        "Mobile app access",
        "Basic analytics",
      ],
      popular: false,
      color: "from-gray-400 to-gray-600",
    },
    {
      name: "Professional",
      commission: "10%",
      features: [
        "Premium listing with badges",
        "Priority support",
        "Advanced analytics",
        "Marketing promotions",
        "Custom branding",
        "Bulk upload tools",
      ],
      popular: true,
      color: "from-purple-500 to-pink-500",
    },
    {
      name: "Enterprise",
      commission: "8%",
      features: [
        "White-label solutions",
        "Dedicated account manager",
        "Custom integrations",
        "Advanced API access",
        "Priority placement",
        "Custom reporting",
      ],
      popular: false,
      color: "from-purple-500 to-pink-500",
    },
  ];

  return (
    <>
      <div className="min-h-screen relative overflow-x-hidden bg-white font-['Inter']">
        <Header overlay />

        {/* ── Hero ─────────────────────────────────────────────────────── */}
        <section className="relative overflow-hidden">
          {/* Background: soft tint, glows and a fine grid */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute inset-0 bg-gradient-to-b from-purple-50 via-white to-white" />
            <div
              className="absolute inset-0 opacity-[0.35]"
              style={{
                backgroundImage:
                  "linear-gradient(to right, #e9d5ff 1px, transparent 1px), linear-gradient(to bottom, #e9d5ff 1px, transparent 1px)",
                backgroundSize: "56px 56px",
                maskImage: "radial-gradient(ellipse at top, black 20%, transparent 70%)",
                WebkitMaskImage: "radial-gradient(ellipse at top, black 20%, transparent 70%)",
              }}
            />
            <div className="absolute -top-32 -left-24 w-[520px] h-[520px] bg-purple-300 rounded-full opacity-25 blur-[110px] animate-float" />
            <div className="absolute -top-16 right-[-10%] w-[560px] h-[560px] bg-pink-300 rounded-full opacity-20 blur-[120px] animate-float-delayed" />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-14 sm:pt-40 sm:pb-20">
            <div className="grid lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] gap-12 lg:gap-16 items-center">

              {/* Left: copy */}
              <div className="text-center lg:text-left">
                <div className="inline-flex items-center gap-2 rounded-full border border-purple-200 bg-white/80 backdrop-blur-sm pl-1.5 pr-4 py-1.5 mb-7 shadow-sm">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-purple-600 to-pink-600">
                    <Star className="w-3.5 h-3.5 text-white fill-white" />
                  </span>
                  <span className="text-gray-800 font-semibold text-xs sm:text-sm">
                    India's #1 Event Marketplace
                  </span>
                </div>

                <h1 className="font-['Manrope'] text-4xl sm:text-5xl xl:text-6xl font-extrabold text-gray-900 leading-[1.05] tracking-tight">
                  Transform Your{" "}
                  <span className="bg-gradient-to-r from-purple-600 to-pink-500 bg-clip-text text-transparent animate-gradient">
                    Event Business
                  </span>{" "}
                  Into a{" "}
                  <span className="relative inline-block">
                    Revenue Machine
                    <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 300 8" fill="none" aria-hidden="true">
                      <path d="M2 6C50 2 100 1 150 3C200 5 250 4 298 2" stroke="url(#underline-grad)" strokeWidth="3" strokeLinecap="round" />
                      <defs>
                        <linearGradient id="underline-grad" x1="0" y1="0" x2="300" y2="0">
                          <stop stopColor="#9333ea" />
                          <stop offset="1" stopColor="#db2777" />
                        </linearGradient>
                      </defs>
                    </svg>
                  </span>
                </h1>

                <p className="mt-7 text-base sm:text-lg text-gray-600 leading-relaxed max-w-xl mx-auto lg:mx-0">
                  Join successful vendors who've boosted their revenue by{" "}
                  <span className="font-bold text-gray-900">300%+</span> with our
                  AI-powered marketplace platform.
                </p>

                <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                  <button
                    className={`${BTN_PRIMARY} h-12 px-6 text-[15px]`}
                    onClick={() => navigate("/vendor-registration")}
                  >
                    Start Earning Today
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                  <button
                    className="group inline-flex h-12 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-6 text-[15px] font-['Manrope'] font-semibold text-gray-900 shadow-sm transition-colors hover:border-purple-200 hover:bg-purple-50"
                    onClick={() => navigate("/vendor-signin")}
                  >
                    Vendor sign in
                  </button>
                </div>

              </div>

              {/* Right: product preview */}
              <div className="relative mx-auto w-full max-w-md lg:max-w-none">
                <div className="absolute -inset-6 rounded-[2.5rem] bg-gradient-to-br from-purple-400/30 to-pink-400/30 blur-3xl" />

                <div className="relative rounded-3xl border border-gray-200/80 bg-white/90 backdrop-blur-sm shadow-2xl shadow-purple-900/10 overflow-hidden">
                  {/* Window bar */}
                  <div className="flex items-center gap-2 border-b border-gray-100 bg-gray-50/80 px-4 py-3">
                    <span className="h-2.5 w-2.5 rounded-full bg-red-300" />
                    <span className="h-2.5 w-2.5 rounded-full bg-amber-300" />
                    <span className="h-2.5 w-2.5 rounded-full bg-green-300" />
                    <span className="ml-3 rounded-md bg-white border border-gray-200 px-2.5 py-0.5 text-[10px] font-medium text-gray-400">
                      Vendor dashboard
                    </span>
                  </div>

                  <div className="p-5 sm:p-6 space-y-4">
                    {/* Headline stat — rotates through the platform numbers */}
                    <div className="flex items-center justify-between gap-4 rounded-2xl bg-gradient-to-br from-purple-600 to-pink-600 p-5 text-white shadow-lg shadow-purple-600/20">
                      <div className="min-w-0">
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-white/75">
                          {stats[currentStat]?.label}
                        </p>
                        <p className="mt-1 font-['Manrope'] text-3xl sm:text-4xl font-extrabold tracking-tight tabular-nums">
                          {stats[currentStat]?.number}
                        </p>
                      </div>
                      <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-inset ring-white/25">
                        {stats[currentStat]?.icon}
                      </div>
                    </div>

                    {/* Growth + chart */}
                    <div className="rounded-2xl border border-gray-200/70 bg-white p-4">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-semibold text-gray-500">Monthly Revenue</p>
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-green-200 bg-green-50 px-2.5 py-1 text-[11px] font-semibold text-green-700">
                          <span className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" />
                          +400% growth this month
                        </span>
                      </div>
                      <div className="flex items-end gap-1.5 mt-4 h-20">
                        {[30, 45, 35, 60, 50, 75, 65, 90, 80, 100, 85, 95].map((h, i) => (
                          <div
                            key={i}
                            className="flex-1 rounded-md bg-gradient-to-t from-purple-500 to-pink-400"
                            style={{ height: `${h}%`, opacity: 0.45 + (i / 11) * 0.55 }}
                          />
                        ))}
                      </div>
                      <div className="flex justify-between mt-2">
                        <span className="text-[10px] font-medium text-gray-400">Jan</span>
                        <span className="text-[10px] font-medium text-gray-400">Dec</span>
                      </div>
                    </div>

                    {/* Activity rows */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="flex items-center gap-3 rounded-2xl border border-gray-200/70 bg-gray-50/70 p-3">
                        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-green-100">
                          <span className="h-2.5 w-2.5 rounded-full bg-green-500 animate-pulse" />
                        </span>
                        <div className="min-w-0">
                          <p className="text-xs font-bold text-gray-900">New Booking!</p>
                          <p className="text-[11px] text-gray-500 truncate">Wedding · ₹85,000</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 rounded-2xl border border-gray-200/70 bg-gray-50/70 p-3">
                        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-pink-100">
                          <Heart className="w-4 h-4 text-pink-500 fill-pink-500" />
                        </span>
                        <div className="min-w-0">
                          <p className="text-xs font-bold text-gray-900">Customer loved it!</p>
                          <div className="flex gap-0.5 mt-0.5">
                            {[...Array(5)].map((_, i) => (
                              <Star key={i} className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>

            {/* Platform numbers */}
            <Reveal className="mt-14 sm:mt-20 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {stats.map((stat) => (
                <div key={stat.label} className="rounded-2xl border border-gray-200/70 bg-white/80 backdrop-blur-sm p-4 text-center shadow-[0_1px_3px_rgba(16,24,40,0.04)]">
                  <div className="mx-auto mb-2.5 flex h-9 w-9 items-center justify-center rounded-xl bg-purple-50 text-purple-600">
                    {stat.icon}
                  </div>
                  <p className="font-['Manrope'] text-xl sm:text-2xl font-extrabold tracking-tight text-gray-900 tabular-nums">{stat.number}</p>
                  <p className="mt-0.5 text-xs font-medium text-gray-500">{stat.label}</p>
                </div>
              ))}
            </Reveal>
          </div>
        </section>

        {/* ── Features ─────────────────────────────────────────────────── */}
        <section id="features" className="py-20 sm:py-28 bg-gray-50 border-y border-gray-100">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHead
              center
              eyebrow="Revolutionary Features"
              title={<>Why Vendors Choose <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Us</span></>}
              text="Experience next-generation tools and features designed to maximize your business potential"
            />

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              {features.map((feature, index) => (
                <Reveal
                  key={index}
                  delay={index * 90}
                  className={`group relative overflow-hidden ${CARD} p-6 sm:p-7 hover:border-purple-200 hover:shadow-xl hover:shadow-purple-900/5 hover:-translate-y-0.5 transition-all duration-300`}
                >
                  <div className="pointer-events-none absolute -top-14 -right-14 h-36 w-36 rounded-full bg-purple-100/0 blur-2xl transition-colors duration-300 group-hover:bg-purple-100/80" />
                  <div className="relative flex items-start justify-between">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center text-white shadow-md shadow-purple-600/20 [&>svg]:w-6 [&>svg]:h-6">
                      {feature.icon}
                    </div>
                    <span className="rounded-full border border-purple-200 bg-purple-50 px-2.5 py-1 text-[11px] font-semibold text-gray-800">
                      {feature.highlight}
                    </span>
                  </div>
                  <h3 className="relative mt-5 font-['Manrope'] text-lg font-bold text-gray-900">
                    {feature.title}
                  </h3>
                  <p className="relative mt-2 text-sm text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </Reveal>
              ))}
            </div>

            {/* Benefits */}
            <div className="mt-4 sm:mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
              {benefits.map((benefit, index) => (
                <Reveal
                  key={index}
                  delay={index * 80}
                  className={`${CARD} p-5 flex items-start gap-3.5 hover:border-purple-200 transition-colors duration-300`}
                >
                  <div className="w-10 h-10 flex-shrink-0 rounded-xl bg-purple-50 flex items-center justify-center text-purple-600 [&>svg]:w-5 [&>svg]:h-5">
                    {benefit.icon}
                  </div>
                  <div className="min-w-0">
                    <h4 className="font-['Manrope'] text-sm font-bold text-gray-900">{benefit.title}</h4>
                    <p className="mt-1 text-xs text-gray-500 leading-relaxed">{benefit.description}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ── Vendor types ─────────────────────────────────────────────── */}
        <section id="vendors" className="py-20 sm:py-28 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHead
              eyebrow="For every business"
              title="Designed for your success"
              text="Whatever your event business, we have the tools and the audience to help you grow. Pick your category to see what you can list."
            />

            <Reveal
              className="grid lg:grid-cols-12 gap-4 lg:gap-6 items-start"
              onMouseEnter={() => setVendorPaused(true)}
              onMouseLeave={() => setVendorPaused(false)}
            >
              {/* Selector */}
              <div className="lg:col-span-5 flex flex-col gap-2.5">
                {vendorTypes.map((vendor, index) => {
                  const active = activeVendor === index;
                  return (
                    <button
                      key={index}
                      onClick={() => setActiveVendor(index)}
                      onMouseEnter={() => setActiveVendor(index)}
                      aria-pressed={active}
                      className={`group relative overflow-hidden text-left w-full flex items-center gap-4 rounded-2xl border p-4 sm:p-5 transition-all duration-300 ${
                        active
                          ? "bg-white border-purple-300 shadow-lg shadow-purple-900/5 ring-4 ring-purple-500/10"
                          : "bg-gray-50/70 border-gray-200/70 hover:bg-white hover:border-purple-200"
                      }`}
                    >
                      <div
                        className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300 [&>svg]:w-6 [&>svg]:h-6 ${
                          active
                            ? "bg-gradient-to-br from-purple-600 to-pink-600 text-white shadow-md shadow-purple-600/20"
                            : "bg-white text-gray-400 border border-gray-200 group-hover:text-purple-600"
                        }`}
                      >
                        {vendor.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-['Manrope'] font-bold text-sm sm:text-base text-gray-900">
                          {vendor.title}
                        </div>
                        <div className="text-xs text-gray-500 truncate mt-0.5">
                          {vendor.items.length} services · {vendor.growth} avg growth
                        </div>
                      </div>
                      <ChevronRight
                        className={`w-5 h-5 flex-shrink-0 transition-all duration-300 ${
                          active ? "text-purple-600 translate-x-0" : "text-gray-300 -translate-x-1"
                        }`}
                      />
                      {/* Auto-rotate progress */}
                      {active && !vendorPaused && (
                        <span key={activeVendor} className="absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-purple-600 to-pink-600 vendor-progress" />
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Detail */}
              <div className="lg:col-span-7">
                {(() => {
                  const vendor = vendorTypes[activeVendor];
                  return (
                    <div
                      key={activeVendor}
                      className="detail-fade relative overflow-hidden rounded-3xl border border-gray-200/70 bg-white shadow-xl shadow-purple-900/5"
                    >
                      <div className="relative overflow-hidden bg-gradient-to-br from-purple-50 via-white to-pink-50 p-6 sm:p-8 border-b border-gray-100">
                        <div className="pointer-events-none absolute -top-20 -right-16 h-56 w-56 rounded-full bg-purple-200/40 blur-3xl" />
                        <div className="relative flex items-start justify-between gap-4">
                          <div className="flex items-center gap-4">
                            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center bg-gradient-to-br from-purple-600 to-pink-600 text-white shadow-md shadow-purple-600/20 [&>svg]:w-7 [&>svg]:h-7">
                              {vendor.icon}
                            </div>
                            <div>
                              <h3 className="font-['Manrope'] text-xl sm:text-2xl font-extrabold tracking-tight text-gray-900">
                                {vendor.title}
                              </h3>
                              <div className="flex items-center gap-1.5 mt-1 text-sm text-gray-500">
                                <MapPin className="w-3.5 h-3.5" /> Available in 500+ cities
                              </div>
                            </div>
                          </div>
                          <div className="hidden sm:flex items-center gap-1.5 rounded-full border border-green-200 bg-green-50 px-3 py-1.5 text-sm font-semibold text-green-700">
                            <TrendingUp className="w-4 h-4" /> {vendor.growth}
                          </div>
                        </div>
                      </div>

                      <div className="p-6 sm:p-8">
                        <div className="text-[11px] font-semibold tracking-wider uppercase text-gray-400 mb-3">
                          What you can list
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 mb-7">
                          {vendor.items.map((item, i) => (
                            <div
                              key={i}
                              className="flex items-center gap-2 rounded-xl border border-gray-200/70 bg-gray-50/70 px-3 py-2.5 text-xs sm:text-sm font-medium text-gray-700"
                            >
                              <span className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-purple-600 text-white">
                                <Check className="w-2.5 h-2.5" strokeWidth={3} />
                              </span>
                              <span className="truncate">{item}</span>
                            </div>
                          ))}
                        </div>

                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-5 border-t border-gray-100">
                          <div className="flex-1">
                            <div className="text-xs font-medium text-gray-500">Typical monthly earnings</div>
                            <div className="font-['Manrope'] text-xl font-extrabold tracking-tight text-gray-900">
                              {vendor.earning}
                            </div>
                          </div>
                          <button
                            onClick={() => navigate("/vendor-registration")}
                            className={`${BTN_PRIMARY} h-12 px-6 text-sm`}
                          >
                            Start as {vendor.title.split(" ")[0]}
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </Reveal>
          </div>
        </section>

        {/* ── How it works ─────────────────────────────────────────────── */}
        <section id="how-it-works" className="py-20 sm:py-28 bg-gray-50 border-y border-gray-100">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHead
              eyebrow="How it works"
              title="Get started in four simple steps"
              text="From registration to your first booking in under 24 hours — no paperwork headaches, no waiting around."
            />

            <div className="relative">
              {/* Connecting line through the step tiles (desktop) */}
              <div className="hidden lg:block absolute top-[3.25rem] left-[12.5%] right-[12.5%] h-px bg-gradient-to-r from-purple-200 via-pink-200 to-purple-200" />

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
                {howItWorks.map((step, index) => (
                  <Reveal
                    key={index}
                    delay={index * 100}
                    className={`group relative ${CARD} p-6 hover:border-purple-200 hover:shadow-xl hover:shadow-purple-900/5 hover:-translate-y-0.5 transition-all duration-300`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="relative z-10 w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-600 to-pink-600 text-white flex items-center justify-center shadow-md shadow-purple-600/20 [&>svg]:w-6 [&>svg]:h-6">
                        {step.icon}
                      </div>
                      <span className="font-['Manrope'] text-4xl font-extrabold tracking-tight text-gray-100 group-hover:text-purple-100 transition-colors tabular-nums">
                        {step.step}
                      </span>
                    </div>
                    <h3 className="mt-5 font-['Manrope'] text-lg font-bold text-gray-900">{step.title}</h3>
                    <p className="mt-2 text-sm text-gray-600 leading-relaxed">{step.description}</p>
                    <div className="mt-5 inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-semibold text-gray-600">
                      <Clock className="w-3.5 h-3.5 text-gray-400" />
                      {step.time}
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── AI engine — vendor + customer ────────────────────────────── */}
        <section id="ai-planner" className="relative overflow-hidden py-20 sm:py-28 bg-gray-950 text-white">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-40 left-1/4 w-[520px] h-[520px] bg-purple-600 rounded-full opacity-25 blur-[130px]" />
            <div className="absolute -bottom-40 right-1/4 w-[520px] h-[520px] bg-pink-600 rounded-full opacity-20 blur-[130px]" />
            <div
              className="absolute inset-0 opacity-[0.06]"
              style={{
                backgroundImage:
                  "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
                backgroundSize: "56px 56px",
              }}
            />
          </div>

          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal className="text-center max-w-2xl mx-auto mb-8 sm:mb-10">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider ring-1 ring-inset ring-white/15 mb-5">
                <Bot className="w-3.5 h-3.5" /> Powered by AI
              </span>
              <h2 className="font-['Manrope'] text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-4 leading-[1.1]">
                {aiTab === "vendor" ? "Meet " : "AI Engine for "}
                <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                  {aiTab === "vendor" ? "VendorLift AI" : "Customers"}
                </span>
              </h2>
              <p className="text-base sm:text-lg text-white/70 leading-relaxed">
                {aiTab === "vendor"
                  ? "Three intelligent modules that automate your entire vendor journey — from profile setup to bookings."
                  : "Three AI modules that help customers plan their perfect wedding — smart, fast, and personalized."}
              </p>
            </Reveal>

            {/* Segmented toggle */}
            <div className="flex justify-center mb-10 sm:mb-12">
              <div className="inline-flex p-1 bg-white/5 ring-1 ring-inset ring-white/10 rounded-xl">
                {[
                  { key: "vendor", label: "For Vendors", icon: BarChart3 },
                  { key: "customer", label: "For Customers", icon: Users },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setAiTab(tab.key)}
                    aria-pressed={aiTab === tab.key}
                    className={`flex items-center gap-2 px-5 sm:px-6 py-2.5 rounded-lg text-sm font-semibold transition-all duration-300 ${
                      aiTab === tab.key
                        ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md shadow-purple-600/30"
                        : "text-white/60 hover:text-white"
                    }`}
                  >
                    <tab.icon className="w-4 h-4" /> {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Module cards */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              {(aiTab === "vendor"
                ? [
                    {
                      icon: <UsersIcon className="w-5 h-5" />,
                      tag: "Profile & Setup",
                      title: "Smart Profile Builder",
                      items: ["Auto-write listing description from Google", "Add photos automatically from Google", "Auto verify business & bank account via API", "Read GSTIN & license docs — no manual typing", "Score profile and show what's missing"],
                    },
                    {
                      icon: <Package2 className="w-5 h-5" />,
                      tag: "AI Package Builder",
                      title: "Package Engine",
                      items: ["Auto-generate service packages instantly", "Upload a file to analyse & generate templates", "Suggest smart pricing tiers", "AI-written package descriptions", "Bundle related services automatically"],
                    },
                    {
                      icon: <Phone className="w-5 h-5" />,
                      tag: "Leads & Bookings",
                      title: "Lead Intelligence",
                      items: ["Score every lead — show which ones are hot", "Auto-reply to customers when you're offline", "Block calendar after booking acceptance", "Auto-cancel unconfirmed bookings", "Prevent double bookings in real-time", "Alert when leads go cold or ignored"],
                    },
                  ]
                : [
                    {
                      icon: <MessageCircle className="w-5 h-5" />,
                      tag: "Wedding Planner",
                      title: "AI Wedding Planner Assistant",
                      items: ["Conversational, chat-based planning assistant", "Suggests a wedding timeline automatically", "Suggests vendors based on budget & location", "Recommends themes and décor styles", "Creates a personalized planning checklist"],
                    },
                    {
                      icon: <IndianRupee className="w-5 h-5" />,
                      tag: "Budget Planner",
                      title: "AI Budget Planner",
                      items: ["Smart cost estimation", "Budget alerts when nearing limits", "Vendor price comparisons", "Auto-adjusts if your budget changes"],
                    },
                    {
                      icon: <Award className="w-5 h-5" />,
                      tag: "Checklist & Matching",
                      title: "Checklist & Vendor Matching",
                      items: ["Book venue & photographer", "Finalize catering menu", "Wedding dress & decor theme selection", "Send invitations & confirm vendors"],
                    },
                  ]
              ).map((item, i) => (
                <Reveal
                  key={`${aiTab}-${i}`}
                  delay={i * 90}
                  className="group rounded-2xl bg-white/[0.04] p-6 ring-1 ring-inset ring-white/10 backdrop-blur-sm hover:bg-white/[0.07] hover:ring-white/20 transition-all duration-300"
                >
                  <div className="flex items-center justify-between mb-5">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br from-purple-600 to-pink-600 text-white shadow-md shadow-purple-600/30">
                      {item.icon}
                    </div>
                    <span className="font-['Manrope'] text-xs font-bold text-white/30 tabular-nums">0{i + 1}</span>
                  </div>
                  <div className="text-[11px] font-semibold tracking-wider uppercase text-white/50 mb-1.5">
                    {item.tag}
                  </div>
                  <h3 className="font-['Manrope'] text-lg font-bold text-white mb-4">{item.title}</h3>
                  <ul className="space-y-2.5">
                    {item.items.map((pt, j) => (
                      <li key={j} className="flex items-start gap-2.5 text-sm text-white/75 leading-snug">
                        <span className="mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-white/10 text-purple-300">
                          <Check className="w-2.5 h-2.5" strokeWidth={3} />
                        </span>
                        <span>{pt}</span>
                      </li>
                    ))}
                  </ul>
                </Reveal>
              ))}
            </div>

            <div className="mt-12 text-center">
              <button
                onClick={() => navigate("/vendor-registration")}
                className="group inline-flex h-12 items-center gap-2 px-7 rounded-xl text-sm font-['Manrope'] font-semibold text-gray-900 bg-white hover:bg-purple-50 transition-colors duration-300 shadow-lg"
              >
                {aiTab === "vendor" ? "Try VendorLift AI" : "Start planning with AI"}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </section>

        {/* ── CTA ──────────────────────────────────────────────────────── */}
        <section className="px-4 py-16 sm:px-6 sm:py-20 lg:px-8 bg-white">
          <div className="relative mx-auto max-w-6xl overflow-hidden rounded-[2rem] bg-gradient-to-br from-purple-600 via-purple-600 to-pink-600 px-6 py-12 text-white shadow-2xl shadow-purple-600/25 sm:px-12 sm:py-16">
            <div className="pointer-events-none absolute -top-24 -right-16 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-28 -left-16 h-72 w-72 rounded-full bg-pink-300/20 blur-3xl" />
            <div
              className="pointer-events-none absolute inset-0 opacity-[0.07]"
              style={{
                backgroundImage:
                  "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
                backgroundSize: "44px 44px",
              }}
            />

            <div className="relative z-10 flex flex-col items-center gap-8 text-center lg:flex-row lg:justify-between lg:gap-12 lg:text-left">
              <div className="max-w-xl">
                <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold ring-1 ring-inset ring-white/20">
                  <span className="h-1.5 w-1.5 rounded-full bg-green-300" />
                  Onboarding vendors in 500+ cities
                </div>
                <h2 className="mt-4 font-['Manrope'] text-3xl font-extrabold leading-[1.1] tracking-tight sm:text-4xl lg:text-5xl">
                  Ready to transform your business?
                </h2>
                <p className="mt-3 text-base text-white/85 sm:text-lg">
                  Join thousands of vendors growing toward{" "}
                  <span className="font-semibold text-white">300%+ revenue</span> — no setup fees,
                  instant onboarding.
                </p>
              </div>

              <div className="flex w-full flex-shrink-0 flex-col items-stretch gap-3 sm:w-auto sm:min-w-[17rem]">
                <button
                  onClick={() => navigate("/vendor-registration")}
                  className="group inline-flex h-14 items-center justify-center gap-2 rounded-2xl bg-white px-6 text-base font-['Manrope'] font-bold text-gray-900 shadow-lg transition-all duration-300 hover:bg-purple-50"
                >
                  Start free trial
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </button>
                <button
                  onClick={() => navigate("/vendor-signin")}
                  className="inline-flex h-12 items-center justify-center rounded-2xl px-6 text-sm font-semibold text-white ring-1 ring-inset ring-white/40 transition hover:bg-white/15"
                >
                  I already have an account
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <SiteFooter />
      </div>
    </>
  );
};

export default MandapAIVendor;
